import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useSpring, useTransform } from 'framer-motion';
import { Users, Send, Check, TrendingUp, Sparkles } from 'lucide-react';

const EASE = [0.21, 0.47, 0.32, 0.98];

/* ---------- Mockup 1: leads populating row-by-row ---------- */
const LeadListMockup = ({ darkMode }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const leads = [
    { name: 'maya.builds', tag: 'MB' },
    { name: 'thefitcoach', tag: 'TF' },
    { name: 'studio.luxe', tag: 'SL' },
    { name: 'grow.with.sam', tag: 'GS' },
    { name: 'urban.eats', tag: 'UE' },
  ];
  const container = { hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } };
  const row = { hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: EASE } } };

  return (
    <div ref={ref} className={`w-full max-w-sm rounded-xl border p-4 ${darkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-gray-200'} shadow-xl`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-500" />
          <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Imported leads</span>
        </div>
        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>6,865</span>
      </div>
      <motion.div variants={container} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="space-y-2">
        {leads.map((l) => (
          <motion.div key={l.name} variants={row} className={`flex items-center gap-3 rounded-lg px-2.5 py-2 ${darkMode ? 'bg-slate-900/60' : 'bg-gray-50'}`}>
            <div className="w-7 h-7 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">{l.tag}</div>
            <span className={`text-sm flex-1 truncate ${darkMode ? 'text-slate-200' : 'text-gray-800'}`}>@{l.name}</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

/* ---------- Mockup 2: DM typing out + sending ---------- */
const DMMockup = ({ darkMode }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const full = "Hey Maya — loved your latest reel. Open to a quick collab idea? 👀";
  const [typed, setTyped] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) { clearInterval(id); setTimeout(() => setSent(true), 400); }
    }, 32);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <div ref={ref} className={`w-full max-w-[320px] rounded-2xl border overflow-hidden ${darkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-gray-200'} shadow-xl`}>
      <div className={`flex items-center gap-2 px-4 py-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white">MB</div>
        <div className="leading-tight">
          <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>maya.builds</div>
          <div className="text-[11px] text-emerald-400">Active now</div>
        </div>
      </div>
      <div className={`p-4 min-h-[140px] flex flex-col justify-end ${darkMode ? 'bg-slate-900/40' : 'bg-gray-50'}`}>
        <div className="flex justify-end">
          <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 text-sm text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow">
            {typed}
            {!sent && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-white/80 animate-pulse" />}
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: sent ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-end gap-1 mt-1.5 text-[11px] text-emerald-400"
        >
          <Check className="w-3 h-3" /> Delivered · human-like delay
        </motion.div>
      </div>
    </div>
  );
};

/* ---------- Mockup 3: replies + reply-rate counting up ---------- */
const RepliesMockup = ({ darkMode }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const spring = useSpring(0, { stiffness: 70, damping: 18 });
  const rate = useTransform(spring, (v) => `${Math.round(v)}%`);
  useEffect(() => { if (inView) spring.set(32); }, [inView]);

  const replies = [
    { name: 'thefitcoach', msg: "Yeah! Tell me more 🙌" },
    { name: 'studio.luxe', msg: "Interested — what's the rate?" },
    { name: 'urban.eats', msg: "DM me the details" },
  ];
  const container = { hidden: {}, visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } } };
  const item = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } } };

  return (
    <div ref={ref} className={`w-full max-w-sm rounded-xl border p-4 ${darkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-gray-200'} shadow-xl`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Replies</span>
        <div className="flex items-center gap-1 text-emerald-400">
          <TrendingUp className="w-4 h-4" />
          <motion.span className="text-sm font-bold">{rate}</motion.span>
          <span className={`text-xs font-normal ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>reply rate</span>
        </div>
      </div>
      <motion.div variants={container} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="space-y-2">
        {replies.map((r) => (
          <motion.div key={r.name} variants={item} className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-slate-900/60' : 'bg-gray-50'}`}>
            <div className={`text-xs font-medium mb-0.5 ${darkMode ? 'text-cyan-300' : 'text-blue-600'}`}>@{r.name}</div>
            <div className={`text-sm ${darkMode ? 'text-slate-200' : 'text-gray-800'}`}>{r.msg}</div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

/* ---------- Section ---------- */
const STEPS = [
  { n: '01', title: 'Import your leads', desc: 'Upload a CSV or pull from your scrapes. Thousands of Instagram prospects, organized into lists in seconds.', Mock: LeadListMockup },
  { n: '02', title: 'AI sends personalized DMs', desc: 'icedms sends opener + follow-ups on autopilot — human-like typing, randomized delays, and daily limits to stay safe.', Mock: DMMockup },
  { n: '03', title: 'Replies land in your inbox', desc: 'Responses are detected and routed to one inbox, with reply rates tracked per campaign so you double down on what works.', Mock: RepliesMockup },
];

const HowItWorks = ({ darkMode }) => {
  return (
    <section className="relative py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="text-center mb-14"
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4 ${darkMode ? 'bg-slate-800 text-cyan-300 border border-slate-700' : 'bg-white text-blue-600 border border-gray-200'} shadow-sm`}>
            <Sparkles className="w-3.5 h-3.5" /> How it works
          </div>
          <h2 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            From cold list to warm replies, on autopilot
          </h2>
        </motion.div>

        <div className="space-y-16 lg:space-y-24">
          {STEPS.map((s, i) => (
            <div key={s.n} className={`flex flex-col gap-8 lg:gap-14 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
              {/* Copy */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex-1 text-center lg:text-left"
              >
                <div className={`text-sm font-bold mb-2 ${darkMode ? 'text-cyan-400' : 'text-blue-500'}`}>{s.n}</div>
                <h3 className={`text-2xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
                <p className={`text-base max-w-md mx-auto lg:mx-0 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{s.desc}</p>
              </motion.div>
              {/* Mockup */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="flex-1 flex justify-center"
              >
                <s.Mock darkMode={darkMode} />
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
